import { useToaster } from '@flintmint/web-shared/hooks/use-toaster';
import { useUpdateUserPreferences, useUserPreferences } from '@/hooks/rxdb-models';

import { Grid } from '@flintmint/web-shared/components/grid';
import { PreferenceCard } from '@/components/preference-card';
import { Alert } from '@flintmint/web-shared/components/alert';
import { Button } from '@flintmint/web-shared/components/button';
import { Switch } from '@flintmint/web-shared/components/switch';

import styles from './personalization.module.scss';

import HeadingPuzzle from './assets/heading-puzzle.svg?react';

const tipsKeys = ['originalBlockEditWarning'] as const;

export const Personalization = () => {
  const { preferences, isUserLoaded } = useUserPreferences();
  const { mutate: updateUserPreferences } = useUpdateUserPreferences();

  const { showToastNotification } = useToaster();

  const areTipsRestored = tipsKeys.every((key) => !preferences?.[key]);

  return (
    <div className={styles.personalization} data-testid="settings-personalization">
      <div className={styles.heading}>
        <h2>Personalization</h2>
        <p>Manage how DocuMint behaves while you work.</p>
        <div className={styles.puzzle}>
          <HeadingPuzzle />
        </div>
      </div>

      <Grid>
        <Grid.Row col={2}>
          <PreferenceCard
            title="Response behavior"
            description="Controls that affect generated responses across the app."
            subTitle="Streaming responses"
            subDescription="Show generated text as it is produced. This may impact performance."
            action={
              isUserLoaded && (
                <Switch
                  checked={preferences?.stream === true}
                  onChange={(checked) => {
                    updateUserPreferences({ stream: checked })?.then(() => {
                      showToastNotification({
                        type: 'success',
                        title: `Streaming responses: ${checked ? 'ON' : 'OFF'}`,
                      });
                    });
                  }}
                />
              )
            }
          />
          <PreferenceCard
            title="Hidden messages"
            description="Restore tips, confirmations, and prompts you chose not to see again."
            subTitle="Dismissed prompts"
            subDescription={`Applies to all dismissed "Don't show this again" messages.`}
            action={
              <Alert
                type="info"
                color="hero"
                icon="alert-01-bulk"
                maxWidth={350}
                title="Restore dismissed messages?"
                message="Tips and prompts you chose not to see again will appear when relevant."
                primaryButton={{
                  children: 'Restore messages',
                  onClick: () => {
                    const nextPreferences = Object.fromEntries(tipsKeys.map((key) => [key, false]));
                    updateUserPreferences(nextPreferences)?.then(() => {
                      showToastNotification({
                        type: 'success',
                        title: 'Tips and prompts will appear when relevant',
                      });
                    });
                  },
                }}
                trigger={
                  <Button color="light" cornerRadius="smooth" disabled={areTipsRestored}>
                    {areTipsRestored ? 'Restored' : 'Restore'}
                  </Button>
                }
              />
            }
          />
        </Grid.Row>
      </Grid>
    </div>
  );
};
